/**
 * Client-side mobile detection hook.
 *
 * Reads the `platform` cookie set by middleware to determine device type.
 * Falls back to window.innerWidth check if cookie is unavailable.
 *
 * Used in place of server-side getIsMobile() to enable ISR/static rendering
 * (avoids headers()/cookies() calls in Server Components).
 */
import {useState, useEffect} from 'react';

const MOBILE_BREAKPOINT = 768;

function getPlatformFromCookie(): boolean | null {
    if (typeof document === 'undefined') return null;

    const match = document.cookie.match(/(?:^|;\s*)platform=([^;]*)/);
    const platform = match?.[1];
    if (!platform) return null;

    return platform === 'mobile' || platform === 'tablet';
}

export function useClientIsMobile(): boolean {
    const [isMobile, setIsMobile] = useState<boolean>(() => {
        // SSR default: use cookie if available, otherwise assume mobile (matches previous default)
        const fromCookie = getPlatformFromCookie();
        if (fromCookie !== null) return fromCookie;

        if (typeof window !== 'undefined') {
            return window.innerWidth < MOBILE_BREAKPOINT;
        }

        return true; // Default to mobile for SSR (matches previous layout.client.tsx default)
    });

    useEffect(() => {
        const fromCookie = getPlatformFromCookie();
        if (fromCookie !== null) {
            setIsMobile(fromCookie);
            return;
        }

        // Fallback: use window width
        const checkMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
        checkMobile();

        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return isMobile;
}

